import { Modal } from 'antd'
import { Button } from 'common/components/Button/Button'
import { Select } from 'common/components/Select/Select'
import { BUTTON_MODIFIER } from 'common/constants/buttonConstants'
import dayjs from 'dayjs'
import { useEffect, useState, type FC } from 'react'
import { useLazyFetchPatientEncountersQuery, type EncounterData } from './transactionsMapping.api'
import styles from './transactionsMapping.module.scss'

const formatEncounterOptionLabel = (encounter: EncounterData): string => {
  const dateLabel = dayjs(encounter.date).format('MM-DD-YYYY')
  const details = encounter.reason ? `${dateLabel} - ${encounter.reason}` : dateLabel

  return `${encounter.eid} - ${details}`
}

type Props = {
  isOpen: boolean
  onClose: () => void
  patientId?: number | null
  selectedEncounterId?: number | null
  errorMessage?: string
  isResolving?: boolean
  confirmButtonText?: string
  onConfirm: (payload: { encounterId: number; encounterUuid: string }) => void
}

export const EncounterSelectModal: FC<Props> = ({
  isOpen,
  onClose,
  patientId,
  selectedEncounterId,
  errorMessage,
  isResolving,
  confirmButtonText = 'Retry',
  onConfirm,
}) => {
  const [selectedEncounter, setSelectedEncounter] = useState<number | null>(null)
  const [fetchEncounters, { data: encountersData, isFetching: isLoadingEncounters }] =
    useLazyFetchPatientEncountersQuery()

  useEffect(() => {
    if (isOpen && patientId) {
      setSelectedEncounter(selectedEncounterId ?? null)
      fetchEncounters({ patientId })
    }
    if (!isOpen) {
      setSelectedEncounter(null)
    }
  }, [isOpen, patientId, selectedEncounterId, fetchEncounters])

  const encounters = Array.isArray(encountersData?.encounters?.data)
    ? encountersData.encounters.data
    : []

  useEffect(() => {
    if (!isOpen || !selectedEncounterId || isLoadingEncounters) return

    const encounterExists = encounters.some((e) => e.eid === selectedEncounterId)
    if (encounterExists) {
      setSelectedEncounter(selectedEncounterId)
    }
  }, [isOpen, selectedEncounterId, isLoadingEncounters, encounters])

  const handleConfirm = () => {
    const encounter = encounters.find((e) => e.eid === selectedEncounter)
    if (selectedEncounter && encounter) {
      onConfirm({ encounterId: selectedEncounter, encounterUuid: encounter.euuid })
    }
  }

  return (
    <Modal
      title='Select Encounter'
      open={isOpen}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
      width={520}
      className={styles.encounterSelectModal}>
      <div className={styles.encounterSelectModalContent}>
        {errorMessage && <p className={styles.modalError}>{errorMessage}</p>}
        <Select
          label='Encounter'
          loading={isLoadingEncounters}
          containerClassName={styles.encounterSelectField}
          style={{ width: '100%' }}
          listOptions={encounters.map((e) => ({
            code: e.eid,
            name: formatEncounterOptionLabel(e),
          }))}
          value={selectedEncounter ?? undefined}
          onChange={(val) => setSelectedEncounter(val)}
          placeholder='Select an encounter'
          required
        />
        <div className={styles.encounterSelectModalActions}>
          <Button
            className={styles.encounterSelectModalButton}
            onClick={onClose}
            modifier={BUTTON_MODIFIER.SECONDARY}>
            Cancel
          </Button>
          <Button
            className={styles.encounterSelectModalButton}
            onClick={handleConfirm}
            disabled={!selectedEncounter || isResolving}
            loading={isResolving}
            modifier={BUTTON_MODIFIER.PRIMARY}>
            {confirmButtonText}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
